import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useRouteMatch } from 'react-router-dom';
import { Link, Navigate, Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import NavBack from 'dashboard/components/NavBack';
import { OptionType } from 'dashboard/components/ReactSelect';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import {
  convertFieldToRelationSchema,
  convertRelationToFieldSchema,
  FieldSchemaEditorSupportedTypes,
  FieldSchemaRelationSchema,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType, AccountTypeAttributes } from 'dashboard/models/account-type';
import { AccountModule, AccountTypeGridLayout } from 'dashboard/models/account-type-grid-layouts';
import { Environment } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Configuration } from 'dashboard/models/grid-layout';
import { Identifier } from 'dashboard/models/identifier';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import { TransactionType } from 'dashboard/models/transaction-type';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import DashboardAccountActionsRoute from 'dashboard/routes/DashboardAccountActionsRoute';
import { AccountLayoutEditor } from 'dashboard/views/DashboardAccountTypesShowView/AccountLayoutEditor';
import AccountTypesFieldTab from 'dashboard/views/DashboardAccountTypesShowView/AccountTypesFieldTab';
import AccountTypeStatusesTab from 'dashboard/views/DashboardAccountTypesShowView/AccountTypeStatusesTab';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { FieldSchema } from 'lib/fields';
import { useExpandCollapseNavbar } from 'lib/hooks/useExpandCollapseNavbar';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { JsonSchemaObject } from 'lib/json-schema';
import { Permission } from 'lib/permissions';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

const MainColumn = styled.div`
  flex: 1 0 710px;
  overflow: auto;
`;

const SettingsContainer = styled.div`
  padding: ${Spacing[7]};
`;

export const RIGHT_COLUMN_PORTAL_ID = 'account-type-portal';
export const TOP_BUTTON_CONTAINER_PORTAL_ID = 'account-type-top-button-container-portal';

interface DashboardAccountTypesShowViewProps {
  accountType: AccountType;
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  dataSchema: JsonSchemaObject;
  currentEnvironment: Environment;
  graph: Graph | undefined;
  graphQueryTemplates: GraphQueryTemplate[];
  gridLayouts: AccountTypeGridLayout[];
  transactionTypes: TransactionType[];
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
  organization: Organization;
  isLoadingStatuses: boolean;
  isSubmittingStatuses: boolean;
  isUpdatingAccountType: boolean;
  handleAddAccountStatus: (name: string) => Promise<void>;
  handleArchiveAccountStatus: (accountStatusId: string) => Promise<void>;
  handleRenameAccountStatus: (accountStatusId: string, name: string) => Promise<void>;
  handleRestoreAccountStatus: (accountStatusId: string) => Promise<void>;
  handleSetDefaultAccountStatus: (accountStatusId: string) => Promise<void>;
  onUpdateAccountType: (
    attributes: AccountTypeAttributes,
    deactivateFieldSchemaKeys: string[],
  ) => Promise<void>;
  onCreateAccountTypeGridLayout: (configuration: Configuration<AccountModule>) => Promise<void>;
  onUpdateAccountTypeGridLayout: (gridLayouts: AccountTypeGridLayout) => Promise<void>;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => unknown;
}

const DashboardAccountTypesShowView = ({
  accountStatuses,
  accountType,
  accountTypes,
  currentEnvironment,
  dataSchema,
  graph,
  graphQueryTemplates,
  gridLayouts,
  transactionTypes,
  enabledInquiryTemplates,
  publishedVerificationTemplates,
  organization,
  isSubmittingStatuses,
  isLoadingStatuses,
  isUpdatingAccountType,
  onUpdateAccountType,
  onCreateAccountTypeGridLayout,
  onUpdateAccountTypeGridLayout,
  showMessage,
  handleAddAccountStatus,
  handleArchiveAccountStatus,
  handleRenameAccountStatus,
  handleRestoreAccountStatus,
  handleSetDefaultAccountStatus,
}: DashboardAccountTypesShowViewProps) => {
  const isAccountsLayoutEditorEnabled = useFeatureFlag('dashboard:accounts-layout-editor');
  const isAccountsShowAccountStatusEnabled = useFeatureFlag(
    'dashboard:accounts-show-account-status',
  );
  const { isExact } = useRouteMatch();

  useExpandCollapseNavbar();

  const { name } = accountType.attributes;

  const [currentName, setCurrentName] = useState(name);

  useEffect(() => {
    setCurrentName(name);
  }, [name]);

  const relationObjectTypeOptions: OptionType<string>[] = useMemo(() => {
    return [
      {
        label: 'Account types',
        value: '',
        options: accountTypes
          .filter(({ id }) => id !== accountType?.id)
          .map((accountType) => ({
            label: accountType.attributes.name,
            value: accountType.id,
          })),
      },
      {
        label: 'Transaction types',
        value: '',
        options: transactionTypes.map((transactionType) => ({
          label: transactionType.attributes.name,
          value: transactionType.id,
        })),
      },
    ];
  }, [accountTypes, transactionTypes, accountType]);

  const handleSaveFieldSchemas = (
    fieldSchemas: FieldSchemaEditorSupportedTypes[],
    identifiers: Identifier[],
    deactivateFieldSchemaKeys: string[],
  ) => {
    const fieldRelationSchema = fieldSchemas.filter(
      ({ type }) => type === RelationSchemaType.RelationSchema,
    ) as FieldSchemaRelationSchema[];

    return onUpdateAccountType(
      {
        ...accountType.attributes,
        'field-schemas': fieldSchemas.filter(
          ({ type }) => type !== RelationSchemaType.RelationSchema,
        ) as FieldSchema[],
        'relation-schemas': convertFieldToRelationSchema(fieldRelationSchema),
        identifiers,
      },
      deactivateFieldSchemaKeys,
    );
  };

  const fieldWithRelationSchemas: FieldSchemaEditorSupportedTypes[] = useMemo(
    () => [
      ...accountType.attributes['field-schemas'],
      ...convertRelationToFieldSchema(accountType.attributes['relation-schemas']),
    ],
    [accountType.attributes],
  );

  const handleSaveName = (name: string) => {
    return onUpdateAccountType(
      {
        ...accountType.attributes,
        name,
      },
      [],
    );
  };

  const handleSaveAccountTypeGridLayout = (gridLayout: AccountTypeGridLayout) => {
    return onUpdateAccountTypeGridLayout(gridLayout);
  };

  const tabs = [
    {
      name: 'Fields',
      path: '/fields',
      contents: (
        <AccountTypesFieldTab
          fieldSchemas={fieldWithRelationSchemas as EditorableSchema[]}
          identifiers={accountType.attributes.identifiers}
          relationObjectTypeOptions={relationObjectTypeOptions}
          isUpdatingAccountType={isUpdatingAccountType}
          onSaveFieldSchemas={handleSaveFieldSchemas}
          showMessage={showMessage}
        />
      ),
    },
    {
      name: 'Actions',
      path: '/actions',
      disabled: !usePermission(Permission.AccountActionView),
      contents: <DashboardAccountActionsRoute />,
    },
  ];

  if (isAccountsShowAccountStatusEnabled) {
    tabs.push({
      name: 'Statuses',
      path: '/statuses',
      contents: (
        <AccountTypeStatusesTab
          accountStatuses={accountStatuses}
          defaultStatusToken={accountType?.relationships['default-account-status'].data.id}
          handleAddAccountStatus={handleAddAccountStatus}
          handleArchiveAccountStatus={handleArchiveAccountStatus}
          handleRenameAccountStatus={handleRenameAccountStatus}
          handleRestoreAccountStatus={handleRestoreAccountStatus}
          handleSetDefaultAccountStatus={handleSetDefaultAccountStatus}
          isSubmitting={isSubmittingStatuses}
          isLoading={isLoadingStatuses}
        />
      ),
    });
  }

  let navBackLink = '/dashboard/account-types';
  // if we're in a subroute and the account layout editor is enabled, nav back to layout editor
  if (!isExact && isAccountsLayoutEditorEnabled) {
    navBackLink = `/dashboard/account-types/${accountType.id}`;
  }

  return (
    <>
      <Helmet title={`${accountType?.attributes?.name} - Types - Accounts`} />
      <Header
        prepend={<NavBack to={navBackLink} />}
        title={
          <DynamicInput
            value={currentName}
            onChange={(e) => {
              setCurrentName(e.target.value);
            }}
            onBlur={() => {
              if (currentName !== name) {
                handleSaveName(currentName);
              }
            }}
          />
        }
      >
        <div id={TOP_BUTTON_CONTAINER_PORTAL_ID} />
        <FeatureFlagged featureFlag='global:enable-platform-connection-dashboard'>
          <DropdownMenu
            trigger={
              <Button aria-label='More actions' type='button' variant='inverse' width='icon'>
                <MoreIcon />
              </Button>
            }
          >
            <DropdownMenu.Item as={Link} to={`/dashboard/platform-connections/${accountType.id}`}>
              Connections
            </DropdownMenu.Item>
          </DropdownMenu>
        </FeatureFlagged>
      </Header>
      <Container>
        <MainColumn>
          <SentryRoutes>
            <Route
              path='/'
              index
              element={
                isAccountsLayoutEditorEnabled ? (
                  <AccountLayoutEditor
                    accountType={accountType}
                    currentEnvironment={currentEnvironment}
                    organization={organization}
                    graph={graph}
                    graphQueryTemplates={graphQueryTemplates}
                    gridLayouts={gridLayouts}
                    dataSchema={dataSchema}
                    enabledInquiryTemplates={enabledInquiryTemplates}
                    publishedVerificationTemplates={publishedVerificationTemplates}
                    fieldSchema={accountType?.attributes?.['field-schemas']}
                    onAccountTypeGridLayoutCreate={onCreateAccountTypeGridLayout}
                    onAccountTypeGridLayoutSave={handleSaveAccountTypeGridLayout}
                  />
                ) : (
                  <Navigate to='settings' replace />
                )
              }
            />
            <Route
              path='settings/*'
              element={
                <SettingsContainer>
                  <RoutedTabbedSection tabs={tabs} />
                </SettingsContainer>
              }
            />
            <Route path='*' element={<Navigate to='.' replace />} />
          </SentryRoutes>
        </MainColumn>
        {/* children render their panels here so they appear outside of the tab hierarchy */}
        <div id={RIGHT_COLUMN_PORTAL_ID} />
      </Container>
    </>
  );
};

export default DashboardAccountTypesShowView;
